import { Avatar, Icon, IconType, Menu, Tooltip } from "@dexibit/core";
import { faSignOut, faUserSecret } from "@fortawesome/pro-regular-svg-icons";
import { type ComponentPropsWithoutRef, useCallback, useMemo, useRef } from "react";

import { useAppSelector } from "../../store";
import { selectLogoutUri } from "../../store/services/config";
import type { UserInfoDTO } from "../../store/services/userInfo/types";
import { useIsImpersonationMode } from "../../hooks/useUserData";
import { intercomHandler } from "../../utils/intercom";
import tokenManager from "../../utils/tokenManager";
import {
  FooterAccount,
  FooterAvatarArea,
  FooterContainer,
  FooterInfo,
  FooterName,
} from "./styles";

const useScrollOnHover = () => {
  const ref = useRef<HTMLDivElement>(null);
  const onMouseEnter = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const span = el.querySelector("span");
    if (!span) return;
    const overflow = span.scrollWidth - el.clientWidth;
    el.style.setProperty("--scroll-distance", overflow > 0 ? `${-overflow}px` : "0px");
  }, []);
  return { ref, onMouseEnter };
};

interface FooterButtonOwnProps {
  isExpanded: boolean;
  isImpersonating: boolean;
  profileName: string | undefined;
  userInfo: UserInfoDTO;
  venueName: string | undefined;
  accountName: string | undefined;
}

type FooterButtonProps = FooterButtonOwnProps & ComponentPropsWithoutRef<"div">;

const FooterButton = ({
  isExpanded,
  isImpersonating,
  profileName,
  userInfo,
  venueName,
  accountName,
  // Drop style-shaped props that @dexibit/core's Menu injects into its buttonComponent
  // — they would otherwise leak onto the underlying DOM element.
  backgroundColor: _backgroundColor,
  ...rest
}: FooterButtonProps & { backgroundColor?: string }) => {
  const initials =
    userInfo.given_name && userInfo.family_name
      ? `${userInfo.given_name.charAt(0)}${userInfo.family_name.charAt(0)}`
      : undefined;

  const nameScroll = useScrollOnHover();
  const accountScroll = useScrollOnHover();

  return (
    <FooterContainer
      {...rest}
      data-cy="profile-menu-button"
      aria-label="Profile menu"
      role="button"
      tabIndex={0}
      style={{ cursor: "pointer" }}
    >
      <FooterAvatarArea>
        {isImpersonating ? (
          <Tooltip title={`You are impersonating user: ${profileName}`}>
            <span style={{ display: "flex" }}>
              <Icon type={IconType.fa} icon={faUserSecret} />
            </span>
          </Tooltip>
        ) : (
          <Avatar
            imageSrc={userInfo.picture}
            initials={initials}
            alt="User avatar"
            size={32}
          />
        )}
      </FooterAvatarArea>
      <FooterInfo isExpanded={isExpanded}>
        {profileName && (
          <FooterName ref={nameScroll.ref} onMouseEnter={nameScroll.onMouseEnter}>
            <span>{profileName}</span>
          </FooterName>
        )}
        {(venueName || accountName) && (
          <FooterAccount
            ref={accountScroll.ref}
            onMouseEnter={accountScroll.onMouseEnter}
          >
            <span>
              {venueName ? `${venueName} \u2022 ` : ""}
              {accountName}
            </span>
          </FooterAccount>
        )}
      </FooterInfo>
    </FooterContainer>
  );
};

interface NavUserFooterProps {
  userInfo: UserInfoDTO | undefined;
  accountName: string | undefined;
  venueName: string | undefined;
  isExpanded: boolean;
}

export const NavUserFooter = ({
  userInfo,
  accountName,
  venueName,
  isExpanded,
}: NavUserFooterProps) => {
  const logoutUri = useAppSelector(selectLogoutUri);
  const isImpersonating = useIsImpersonationMode();
  const profileName = userInfo?.name || userInfo?.email;

  const handleLogout = () => {
    tokenManager.removeToken();
    intercomHandler.shutdownIntercom();
    if (!logoutUri) return;
    window.location.href = logoutUri;
  };

  // Stable ref so the memoised component always reads fresh props
  const propsRef = useRef<FooterButtonOwnProps>({
    isExpanded,
    isImpersonating,
    profileName,
    userInfo: userInfo!,
    venueName,
    accountName,
  });
  propsRef.current = {
    isExpanded,
    isImpersonating,
    profileName,
    userInfo: userInfo!,
    venueName,
    accountName,
  };

  // Stable component identity prevents Menu from remounting the button each render
  const StableButton = useMemo(
    () =>
      function MenuButton(props: any) {
        return <FooterButton {...props} {...propsRef.current} />;
      },
    [],
  );

  if (!userInfo) return null;

  return (
    <Menu
      id="profile-menu"
      buttonComponent={StableButton}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      items={[
        {
          id: "logout",
          label: "Log out",
          startAdornment: [
            <Icon key="logout-icon" type={IconType.fa} icon={faSignOut} />,
          ],
          onClick: handleLogout,
        },
      ]}
    />
  );
};
